
import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useThemeMode } from "@/hooks/useThemeMode";

interface Futuristic3DCardProps {
  children: React.ReactNode;
  className?: string;
  glowColor?: "cyan" | "purple" | "pink" | "orange" | "green" | "blue";
  intensity?: "light" | "medium" | "heavy";
  animated?: boolean;
  hoverEffect?: boolean;
  depth?: "shallow" | "medium" | "deep";
  delay?: number;
}

export const Futuristic3DCard: React.FC<Futuristic3DCardProps> = ({
  children,
  className,
  glowColor = "cyan",
  intensity = "medium",
  animated = true,
  hoverEffect = true,
  depth = "medium",
  delay = 0,
}) => {
  const { isDark } = useThemeMode();

  const glowColors = {
    cyan: "rgba(14, 165, 233, 0.4)",
    purple: "rgba(139, 92, 246, 0.4)",
    pink: "rgba(236, 72, 153, 0.4)",
    orange: "rgba(245, 158, 11, 0.4)",
    green: "rgba(16, 185, 129, 0.4)",
    blue: "rgba(59, 130, 246, 0.4)",
  };

  const depthStyles = {
    shallow: "shadow-lg shadow-black/30",
    medium: "shadow-xl shadow-black/40",
    deep: "shadow-2xl shadow-black/50",
  };

  const intensityStyles = {
    light: isDark 
      ? "bg-slate-900/90 border-slate-600/70" 
      : "bg-white/95 border-slate-400/70",
    medium: isDark 
      ? "bg-slate-900/95 border-slate-500/80" 
      : "bg-white/98 border-slate-500/80",
    heavy: isDark 
      ? "bg-slate-800/98 border-slate-400/90" 
      : "bg-white/99 border-slate-600/90",
  };

  return (
    <motion.div
      initial={animated ? { opacity: 0, y: 20, scale: 0.95, rotateX: 10 } : undefined}
      animate={animated ? { opacity: 1, y: 0, scale: 1, rotateX: 0 } : undefined}
      whileHover={hoverEffect ? { 
        y: -8, 
        scale: 1.02, 
        rotateX: 5,
        rotateY: 2,
        transition: { duration: 0.3, ease: "easeOut" }
      } : undefined}
      transition={{ 
        duration: 0.6, 
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: delay / 1000
      }}
      className={cn(
        "relative rounded-2xl border-2 overflow-hidden",
        "transform-gpu perspective-1000",
        intensityStyles[intensity],
        depthStyles[depth],
        "transition-all duration-300",
        className
      )}
      style={{
        boxShadow: `0 0 25px ${glowColors[glowColor]}, inset 0 1px 0 ${isDark ? 'rgba(255, 255, 255, 0.10)' : 'rgba(255, 255, 255, 0.95)'}`,
        transform: "translateZ(0)",
      }}
    >
      {/* Sharp animated floating background */}
      <motion.div
        animate={animated ? {
          y: [0, -4, 0],
          opacity: [0.2, 0.4, 0.2],
        } : undefined}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: (delay / 1000) + 0.5
        }}
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${glowColors[glowColor]} 0%, transparent 50%)`,
        }}
      />

      {/* Enhanced sharp reflection effect */}
      <div className={cn(
        "absolute inset-0 pointer-events-none",
        isDark 
          ? "bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-80"
          : "bg-gradient-to-br from-white/90 via-transparent to-transparent opacity-90"
      )} />
      
      {/* Sharp hover border */}
      <div 
        className="absolute inset-0 rounded-2xl border-2 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          borderColor: glowColors[glowColor],
        }}
      />
      
      {/* Content with enhanced contrast */}
      <div className="relative z-10">
        {children}
      </div>
    </motion.div>
  );
};
